import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DialogAction from '../components/Dialog/DialogAction';
import { FieldControllerTypes } from '../components/form_v2/types';
import TableGridWithFilters from '../components/TablegridWithFilters/TableGridWithFilters/TableGridWithFilters';
import Theme from '../Themes/main.theme';
import PageviewIcon from '@mui/icons-material/Pageview';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  RequestDict,
  RequestStatus,
} from '../components/dictionaries/RequestStatus';
import moment from 'moment';
import HttpService from '../services/HttpService';
import { useTranslation } from 'react-i18next';
import { myApplicationsInitialValues } from '../lib/static/applications/filtersInitialValues';
import i18n from '../i18n';
import { GetSortOrder, transformGreekToEnglish } from '../lib/helperFuncs';

const MyApplications = () => {
  const [open, setOpen] = useState(false);
  const [row, setRow] = useState<{ id: number; category: string }>({
    id: 0,
    category: '',
  });
  const [titles, setTitles] = useState<{ key: string; value: number }[]>([]);
  const [administrativeUnits, setAdministrativeUnits] = useState<
    { key: string; value: number }[]
  >([]);
  const [statuses, setsStatuses] = useState<{ key: string; value: number }[]>(
    []
  );
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [reFetch, setRefetch] = useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();

  document.title = t('Αιτήματα');

  const filterFields = [
    [
      {
        control: FieldControllerTypes.autocomplete,
        type: 'autocomplete',
        label: t('Τίτλος Αιτήματος'),
        name: 'title',
        filtersDesign: true,
        options: titles,
      },
      {
        control: FieldControllerTypes.autocomplete,
        type: 'autocomplete',
        label: t('Αρμόδια Οργανική Μονάδα'),
        name: 'administrative_unit_id',
        filtersDesign: true,
        options: administrativeUnits,
      },
      {
        control: FieldControllerTypes.select,
        label: t('Κατάσταση Αιτήματος'),
        name: 'status',
        customLabel: true,
        filtersDesign: true,
        options: statuses,
      },
    ],
  ];

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: t('Τίτλος Αιτήματος') || '-',
      minWidth: 380,
      renderCell: ({ row }) =>
        i18n.language === 'en'
          ? transformGreekToEnglish(t(row.title)) || '-'
          : row.title || '-',
    },
    {
      field: 'section',
      headerName: t('Τομέας') || '-',
      minWidth: 250,
      renderCell: ({ row }) => t(row.section) || '-',
    },
    {
      field: 'administrative_unit',
      headerName: t('Αρμόδια Οργανική Μονάδα') || '-',
      minWidth: 300,
      renderCell: ({ row }) =>
        i18n.language === 'en'
          ? transformGreekToEnglish(t(row.administrative_unit)) || '-'
          : row.administrative_unit || '-',
    },
    {
      field: 'submit_date',
      headerName: t('Ημερομηνία Υποβολής') || '-',
      minWidth: 220,
      renderCell: (params) =>
        params.row.submit_date
          ? moment(params.row.submit_date)
            .format('DD/MM/YYYY, HH:mm:ss')
            .toLocaleString() //'default', { hour12: false }
          : null,
    },
    {
      field: 'status',
      headerName: t('Κατάσταση Αιτήματος') || undefined,
      minWidth: 205,
      sortable: false,
      renderCell: (params) => t(RequestDict[params.row.status]),
    },
    {
      field: 'action_admin',
      headerName: t('Ενέργειες') || undefined,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box>
            <Tooltip
              PopperProps={{
                disablePortal: true,
              }}
              title={t('Προβολή Αιτήματος')}
            >
              <IconButton
                color='primary'
                onClick={() => {
                  navigate(
                    params.row.category === 'professionals'
                      ? `/professionals/view/${params.row.id}`
                      : params.row.category === 'location'
                        ? `/view/${params.row.id}`
                        : params.row.category === 'filming'
                          ? `/view-interest/${params.row.id}`
                          : `/applications/details/${params.row.id}`
                  );
                }}
              >
                <PageviewIcon />
              </IconButton>
            </Tooltip>
            <Tooltip
              PopperProps={{
                disablePortal: true,
              }}
              title={t('Επεξεργασία Αιτήματος')}
            >
              <IconButton
                color='primary'
                onClick={() =>
                  navigate(`/${params.row.category}/edit/${params.row.id}`)
                }
                disabled={
                  params.row.status === RequestStatus.Successful ||
                  params.row.status === RequestStatus.Rejected ||
                  params.row.status === RequestStatus.In_Progress ||
                  (params.row.category !== 'professionals' &&
                    params.row.category !== 'location')
                }
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip
              PopperProps={{
                disablePortal: true,
              }}
              title={t('Διαγραφή Αιτήματος')}
            >
              <IconButton
                color='error'
                onClick={() => {
                  setOpen(true);
                  setRow({ id: params.row.id, category: params.row.category });
                }}
                disabled={params.row.status !== RequestStatus.Draft}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const onDelete = async () => {
    try {
      await HttpService.getAxiosClient().delete(
        `${process.env.REACT_APP_BACKEND_URL}/applications/${
          row.category === 'location'
            ? 'locations'
            : 'professionals'
        }/${row.id}`
      );
      enqueueSnackbar('Επιτυχής διαγραφή του αιτήματός σας', {
        variant: 'success',
      });
      setRefetch(!reFetch);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        'Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα.',
        { variant: 'error' }
      );
    }
  };

  const getStatuses = async () => {
    const response = await HttpService.getAxiosClient().get(
      `${process.env.REACT_APP_BACKEND_URL}/users-parametrics/status`
    );
    response.data.status.unshift({ key: t('Όλες'), value: 'all' });

    const statuses = response.data.status.map((status: any) => {
      return {
        key: t(status.key),
        value: status.value,
      };
    });
    setsStatuses(
      i18n.language === 'en'
        ? statuses.sort(GetSortOrder('key'))
        : statuses.sort((a: any, b: any) => a.key.localeCompare(b.key, 'el'))
    );
  };

  const getAdminUnits = async () => {
    const response = await HttpService.getAxiosClient().get(
      `${process.env.REACT_APP_BACKEND_URL}/users-parametrics/administrative_unit`
    );
    const units = response.data.units.map((unit: any) => {
      return {
        key:
          i18n.language === 'en'
            ? transformGreekToEnglish(t(unit.key))
            : unit.key,
        value: unit.value,
      };
    });

    setAdministrativeUnits(
      i18n.language === 'en'
        ? units.sort(GetSortOrder('key'))
        : units.sort((a: any, b: any) => a.key.localeCompare(b.key, 'el'))
    );
  };

  const getTitles = async () => {
    const response = await HttpService.getAxiosClient().get(
      `${process.env.REACT_APP_BACKEND_URL}/users-parametrics/titles`
    );
    const titles = response.data.titles.map((title: any) => {
      return {
        key:
          i18n.language === 'en'
            ? transformGreekToEnglish(t(title.key))
            : title.key,
        value: title.value,
      };
    });
    setTitles(
      i18n.language === 'en'
        ? titles.sort(GetSortOrder('key'))
        : titles.sort((a: any, b: any) => a.key.localeCompare(b.key, 'el'))
    );
  };

  useEffect(() => {
    getAdminUnits();
    getTitles();
    getStatuses();
  }, []);

  return (
    <>
      {/* <Box mt={2} m={2}>
        <BackButton onClick={() => navigate(-1)}/>
      </Box> */}
      <Typography variant='h2' color={Theme.labelColor?.main}
        marginBottom={2}>
        {t('Αιτήματα')}
      </Typography>
      <Divider sx={{ mb: 5, border: 1, color: Theme?.dividerColor?.main }} />
      <TableGridWithFilters
        rowsPerPageOptions={[15, 30, 50]}
        pageSize={15}
        responseDataFieldName='data'
        sortingMode='server'
        paginationMode='server'
        responseTotalCountFieldName='total_count'
        sortModel={[{ field: 'submit_date', sort: 'desc' }]}
        initialValues={myApplicationsInitialValues}
        filterFields={filterFields}
        reFetch={reFetch}
        columns={columns}
        resetFilters={true}
        fetchUrl={`${process.env.REACT_APP_BACKEND_URL}/applications/locations/applicant`}
      />
      <DialogAction
        open={open}
        title={'Διαγραφή Αιτήματος'}
        handleClose={() => {
          setOpen(false);
        }}
        mode='delete'
        content='Είστε σίγουρος/η ότι θέλετε να διαγράψετε το αίτημα;'
        onAccept={() => {
          onDelete();
          setOpen(false);
        }}
      />
    </>
  );
};

export default MyApplications;
